import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, Check, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Commercial Contract Acquisition",
  "Lead Generation Systems",
  "AI Automations",
  "Not sure yet — let's talk",
];

const inputClass =
  "w-full bg-white/5 border border-white/10 text-white placeholder-white/30 px-4 py-3.5 rounded-none focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition-colors";

const todayStr = () => new Date().toISOString().split("T")[0];
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service_interest: SERVICES[0], slot_date: todayStr(), slot_time: "", message: "",
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isWeekend, setIsWeekend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadSlots = useCallback(async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    try {
      const { data } = await axios.get(`${API}/availability`, { params: { date } });
      setSlots(data.slots || []);
      setIsWeekend(data.is_weekend);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => { loadSlots(form.slot_date); }, [form.slot_date, loadSlots]);

  const pickDate = (e) => setForm((f) => ({ ...f, slot_date: e.target.value, slot_time: "" }));
  const pickSlot = (t) => setForm((f) => ({ ...f, slot_time: t }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!form.slot_time) {
      toast.error("Please pick a time slot for your consultation.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Booked! Check your email for confirmation.");
    } catch (err) {
      const conflict = err?.response?.status === 409;
      toast.error(conflict ? "That time slot was just taken. Please pick another." : "Something went wrong. Please try again or call us.");
      if (conflict) loadSlots(form.slot_date);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 md:pt-52 pb-16">
        <Reveal>
          <div className="overline text-gold mb-8 flex items-center gap-3">
            <span className="h-px w-10 bg-gold" /> Book a Free Consultation
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-4xl">
            Let's win your next <span className="italic text-gold">contract.</span>
          </h1>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-32 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <p className="text-white/60 text-lg leading-relaxed mb-12">
              Tell us about your business and the commercial work you want to win. We'll map out how the acquisition engine fits your goals — no pressure, no cost.
            </p>
            <div className="space-y-6">
              <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-12 h-12 border border-white/10 flex items-center justify-center text-gold group-hover:border-gold transition-colors">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="overline text-white/40">Call</div>
                  <div className="text-white group-hover:text-gold transition-colors">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="w-12 h-12 border border-white/10 flex items-center justify-center text-gold group-hover:border-gold transition-colors">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="overline text-white/40">Email</div>
                  <div className="text-white group-hover:text-gold transition-colors break-all">{CONTACT.email}</div>
                </div>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {done ? (
            <motion.div
              data-testid="contact-success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-gold/40 bg-gold/5 p-12 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-gold text-black flex items-center justify-center mx-auto mb-6">
                <Check size={28} />
              </div>
              <h3 className="font-serif text-3xl md:text-4xl mb-4">Request received.</h3>
              <p className="text-white/60 leading-relaxed">
                Thanks, {form.name.split(" ")[0]}. Your consultation is booked for{" "}
                <span className="text-gold">{form.slot_date} at {fmtTime(form.slot_time)} MT</span>. A confirmation is on its way to your inbox.
              </p>
            </motion.div>
          ) : (
            <Reveal>
              <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-white/50 block mb-2">Full name *</label>
                    <input data-testid="input-name" className={inputClass} value={form.name} onChange={update("name")} placeholder="Jane Contractor" />
                  </div>
                  <div>
                    <label className="overline text-white/50 block mb-2">Email *</label>
                    <input data-testid="input-email" type="email" className={inputClass} value={form.email} onChange={update("email")} placeholder="you@company.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-white/50 block mb-2">Phone</label>
                    <input data-testid="input-phone" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000" />
                  </div>
                  <div>
                    <label className="overline text-white/50 block mb-2">Company</label>
                    <input data-testid="input-company" className={inputClass} value={form.company} onChange={update("company")} placeholder="Your business" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-white/50 block mb-2">Interested in</label>
                    <select data-testid="input-service" className={`${inputClass} appearance-none`} value={form.service_interest} onChange={update("service_interest")}>
                      {SERVICES.map((s) => <option key={s} value={s} className="bg-[#0F0F13]">{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline text-white/50 block mb-2">Consultation date</label>
                    <input data-testid="input-date" type="date" min={todayStr()} className={`${inputClass} [color-scheme:dark]`} value={form.slot_date} onChange={pickDate} />
                  </div>
                </div>
                <div>
                  <label className="overline text-white/50 mb-3 flex items-center gap-2">
                    <Clock size={13} className="text-gold" /> Pick a time <span className="text-white/30 normal-case tracking-normal">(Mountain Time)</span>
                  </label>
                  {loadingSlots ? (
                    <div className="text-white/40 text-sm py-4">Loading available times…</div>
                  ) : isWeekend ? (
                    <div data-testid="weekend-notice" className="text-white/50 text-sm py-4 border border-white/10 px-4 bg-white/5">Weekends are unavailable — please choose a weekday (Mon–Fri).</div>
                  ) : slots.length === 0 ? (
                    <div className="text-white/40 text-sm py-4">No times available for this date.</div>
                  ) : (
                    <div data-testid="slot-grid" className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button
                          type="button"
                          key={s.time}
                          data-testid={`slot-${s.time}`}
                          disabled={!s.available}
                          onClick={() => pickSlot(s.time)}
                          className={`px-2 py-3 text-sm border transition-colors ${
                            form.slot_time === s.time
                              ? "bg-gold text-black border-gold"
                              : s.available
                              ? "border-white/10 text-white/80 hover:border-gold hover:text-gold"
                              : "border-white/5 text-white/20 line-through cursor-not-allowed"
                          }`}
                        >
                          {fmtTime(s.time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="overline text-white/50 block mb-2">What do you want to win?</label>
                  <textarea data-testid="input-message" rows={4} className={inputClass} value={form.message} onChange={update("message")} placeholder="Tell us about the commercial contracts you're after..." />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gold text-black overline px-10 py-5 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Book My Consultation"}
                  {!loading && <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
